import NewNotesButton from '@/components/NewNotesButton'
import { NoteStatEntry } from '@/atoms/stats'
import { Button } from '@/components/ui/button'
import { isReplyNoteEvent } from '@/lib/event'
import { batchDebounce, isTouchDevice } from '@/lib/utils'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import { startLogin, useCurrentPubkey } from '@/atoms/session'
import { useGroupedNotesReadStatus } from '@/hooks/useGroupedNotesReadStatus'
import client from '@/services/client.service'
import { TFeedSubRequest } from '@/types'
import { Event, NostrEvent } from '@nostr/tools/wasm'
import { mergeReverseSortedLists } from '@nostr/tools/utils'
import * as kinds from '@nostr/tools/kinds'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { toast } from 'sonner'
import CompactedEventCard from '../CompactedEventCard'
import { getPinBuryState } from '@/atoms/pinBury'
import { includeRepliesAtom } from '@/atoms/feed'

type TNoteGroup = {
  topNote: NostrEvent
  allNotes: NostrEvent[]
  totalNotes: number
  oldestTimestamp: number
  newestTimestamp: number
  allNoteTimestamps: number[]
}

const GroupedNoteList = forwardRef(
  (
    {
      subRequests,
      showKinds,
      filterMutedNotes = true,
      showRelayCloseReason = false,
      onNotesLoaded,
      userFilter = '',
      filterFn
    }: {
      subRequests: TFeedSubRequest[]
      showKinds: number[]
      filterMutedNotes?: boolean
      showRelayCloseReason?: boolean
      onNotesLoaded?: (
        hasNotes: boolean,
        hasReplies: boolean,
        notesCount: number,
        repliesCount: number
      ) => void
      userFilter?: string
      filterFn?: (event: Event) => boolean
    },
    ref
  ) => {
    const { t } = useTranslation()
    const pubkey = useCurrentPubkey()
    const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
    const includeReplies = useAtomValue(includeRepliesAtom)
    const { markLastNoteRead, markAllNotesRead, getReadStatus, getUnreadCount, markAsUnread } =
      useGroupedNotesReadStatus()
    const [events, setEvents] = useState<Event[]>([])
    const [newEvents, setNewEvents] = useState<Event[]>([])
    const [loading, setLoading] = useState(true)
    const [refreshCount, setRefreshCount] = useState(0)
    const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
    const [matchingPubkeys, setMatchingPubkeys] = useState<Set<string> | null>(null)
    const supportTouch = useMemo(() => isTouchDevice(), [])
    const topRef = useRef<HTMLDivElement | null>(null)
    const [showNewNotesButton, setShowNewNotesButton] = useState(false)
    const [lastButtonClickTime, setLastButtonClickTime] = useState<number>(0)
    const [isFirstRun, setIsFirstRun] = useState(true)
    const eventsToPreserveOnRefresh = useRef<Event[]>([]) // Events to merge after refresh

    const [noteGroups, setNoteGroups] = useState<TNoteGroup[]>([])

    const shouldHideEvent = useCallback(
      (evt: Event) => {
        if (!includeReplies && isReplyNoteEvent(evt)) return true
        if (filterMutedNotes && mutePubkeySet.has(evt.pubkey)) return true

        if (filterFn && !filterFn(evt)) return true

        return false
      },
      [filterFn, filterMutedNotes, mutePubkeySet, includeReplies]
    )

    // refs kept in sync with the latest committed values so that the debounced
    // handleNew always reads fresh state without needing to be recreated (the
    // live subscription that calls it is only re-created on refresh)
    const noteGroupsRef = useRef(noteGroups)
    const pubkeyRef = useRef(pubkey)
    const shouldHideRef = useRef(shouldHideEvent)
    noteGroupsRef.current = noteGroups
    pubkeyRef.current = pubkey
    shouldHideRef.current = shouldHideEvent

    const filteredEvents = useMemo(
      () => events.filter((evt) => !shouldHideEvent(evt)),
      [events, shouldHideEvent]
    )

    useEffect(() => {
      // group events by author pubkey
      let noteGroups: TNoteGroup[] = []
      const authorIndexes = new Map<string, number>()

      for (let i = 0; i < filteredEvents.length; i++) {
        const event = filteredEvents[i]

        const idx = authorIndexes.get(event.pubkey)
        if (idx !== undefined) {
          const group = noteGroups[idx]
          group.allNotes.push(event)
          group.allNoteTimestamps.push(event.created_at)
          group.oldestTimestamp = event.created_at
          group.totalNotes++
        } else {
          authorIndexes.set(event.pubkey, noteGroups.length)
          noteGroups.push({
            allNotes: [event],
            allNoteTimestamps: [event.created_at],
            newestTimestamp: event.created_at,
            oldestTimestamp: event.created_at,
            topNote: event,
            totalNotes: 1
          })
        }
      }

      // sort final notes by pin/bury state (everything is already sorted by created_at descending)
      const pinned: TNoteGroup[] = []
      const buried: TNoteGroup[] = []
      for (let i = noteGroups.length - 1; i >= 0; i--) {
        const group = noteGroups[i]
        switch (getPinBuryState(group.topNote.pubkey)) {
          case 'pinned':
            pinned.push(group)
            noteGroups.splice(i, 1)
            break
          case 'buried':
            buried.push(group)
            noteGroups.splice(i, 1)
            break
        }
      }
      noteGroups = [...pinned.reverse(), ...noteGroups, ...buried.reverse()]

      setNoteGroups(noteGroups)
    }, [filteredEvents, getPinBuryState])

    // update matching pubkeys when user filter changes
    useEffect(() => {
      if (!userFilter.trim()) {
        setMatchingPubkeys(null)
        return
      }

      const searchProfiles = async () => {
        try {
          const pubkeys = client.searchPubKeysFromLocal(userFilter, 1000)
          setMatchingPubkeys(new Set(pubkeys))
        } catch (error) {
          console.error('Error searching profiles:', error)
          setMatchingPubkeys(new Set())
        }
      }

      searchProfiles()
    }, [userFilter])

    // apply author name filter
    const nameFilteredGroups = useMemo(() => {
      if (matchingPubkeys === null) return noteGroups

      return noteGroups.filter((group) => matchingPubkeys.has(group.topNote.pubkey))
    }, [noteGroups, matchingPubkeys])

    useEffect(() => {
      if (newEvents.length === 0) {
        setShowNewNotesButton(false)
        return
      }

      const now = Date.now()
      const timeSinceLastClick = now - lastButtonClickTime

      // Show immediately if more than 50 events
      if (newEvents.length > 50) {
        setShowNewNotesButton(true)
        return
      }

      // Don't show within 60 seconds, unless 20+ events
      if (timeSinceLastClick < 60000 && newEvents.length < 20) {
        setShowNewNotesButton(false)
        return
      }

      // Show after delay (5s first run, 45s next runs)
      const delay = isFirstRun ? 3000 : 45000
      const timeoutId = setTimeout(() => {
        setShowNewNotesButton(true)
      }, delay)
      return () => clearTimeout(timeoutId)
    }, [newEvents.length, lastButtonClickTime, isFirstRun])

    // notify parent about notes composition (notes vs replies)
    useEffect(() => {
      if (!onNotesLoaded || loading || events.length === 0) return

      const notesCount = events.filter((evt) => !isReplyNoteEvent(evt)).length
      const repliesCount = events.filter((evt) => isReplyNoteEvent(evt)).length
      const hasNotes = notesCount > 0
      const hasReplies = repliesCount > 0

      onNotesLoaded(hasNotes, hasReplies, notesCount, repliesCount)
    }, [events, loading, onNotesLoaded])

    const scrollToTop = (behavior: ScrollBehavior = 'instant') => {
      setTimeout(() => {
        topRef.current?.scrollIntoView({ behavior, block: 'start' })
      }, 20)
    }

    const refresh = () => {
      // Save current events to preserve them across refresh (will be filtered by timeframe after merge)
      eventsToPreserveOnRefresh.current = [...newEvents, ...events].sort(
        (a, b) => b.created_at - a.created_at
      )

      // Reset throttling state so new events from refresh can appear quickly
      setShowNewNotesButton(false)
      setLastButtonClickTime(Date.now())
      setIsFirstRun(true)

      scrollToTop()
      setTimeout(() => {
        setRefreshCount((count) => count + 1)
      }, 500)
    }

    useEffect(() => {
      function handler(this: EventTarget, evt: globalThis.Event) {
        const eventId = (evt as unknown as CustomEvent<string>).detail
        setEvents((events) => events.filter((e) => e.id !== eventId))
        setNewEvents((events) => events.filter((e) => e.id !== eventId))
      }

      client.addEventListener('eventRemoved', handler)
      return () => client.removeEventListener('eventRemoved', handler)
    }, [])

    useImperativeHandle(ref, () => ({ scrollToTop, refresh }), [])

    useEffect(() => {
      if (!subRequests.length) return

      setLoading(true)
      setEvents([])
      setNewEvents([])

      if (showKinds.length === 0) {
        setLoading(false)
        return () => {}
      }

      const groupedNotesSince = Math.floor(
        Date.now() / 1000 - window.hallway.universe.groupedTimeframe
      )

      const subc = client.subscribeTimeline(
        subRequests,
        {
          kinds: showKinds,
          since: groupedNotesSince
        },
        {
          async onEvents(events, isFinal) {
            if (isFinal) {
              setLoading(false)

              // Merge with preserved events from refresh and filter by timeframe
              if (eventsToPreserveOnRefresh.current.length > 0) {
                const combined = [...events, ...eventsToPreserveOnRefresh.current]
                // Remove duplicates by event id
                const uniqueEvents = Array.from(
                  new Map(combined.map((evt) => [evt.id, evt])).values()
                )
                // Filter by timeframe and sort
                events = uniqueEvents
                  .filter((evt) => evt.created_at >= groupedNotesSince)
                  .sort((a, b) => b.created_at - a.created_at)

                eventsToPreserveOnRefresh.current = []
              }
            }

            if (events.length > 0) {
              setEvents(events)
            }
          },
          onNew: handleNew,
          onClose(url, reason) {
            if (!showRelayCloseReason) return
            // ignore reasons from @nostr/tools
            if (
              [
                'closed by caller',
                'relay connection errored',
                'relay connection closed',
                'pingpong timed out',
                'relay connection closed by us'
              ].includes(reason)
            ) {
              return
            }

            console.warn(`${url}: ${reason}`)
            toast.warning(`${url}: ${reason}`)
          }
        },
        {
          startLogin
        }
      )

      return () => {
        handleNew.cancel()
        subc.close()
      }
    }, [subRequests, refreshCount, showKinds])

    // one stable debounced instance for the whole lifetime of the component.
    // it reads the latest state through refs so it never goes stale even though
    // the live subscription that calls it is only re-created on refresh.
    const handleNew = useMemo(
      () =>
        batchDebounce(
          (newEvents: NostrEvent[]) => {
            const curr = noteGroupsRef.current
            const pubkey = pubkeyRef.current
            const shouldHideEvent = shouldHideRef.current

            const pending: NostrEvent[] = []
            const appended: NostrEvent[] = []

            for (let i = 0; i < newEvents.length; i++) {
              const newEvent = newEvents[i]

              // TODO: figure out where exactly the viewport is: for now just assume it's at the top
              if (
                curr.length < 7 ||
                newEvent.created_at < curr[6].topNote.created_at ||
                curr.slice(0, 6).some((group) => group.topNote.pubkey === newEvent.pubkey)
              ) {
                // if there are very few events in the viewport or the new events would be inserted below
                // or they authored by any of the top authors (but they wouldn't be their top notes), just append
                appended.push(newEvent)
              } else if (pubkey && newEvent.pubkey === pubkey) {
                // our own notes are also inserted regardless of any concern
                appended.push(newEvent)
              } else if (shouldHideEvent(newEvent)) {
                // filtered-out notes are stored silently so they appear if the filter changes
                appended.push(newEvent)
              } else {
                // any other "new" notes that would be inserted above, make them be pending in the modal thing
                pending.push(newEvent)
              }
            }

            // prepend them to the top (no need to sort as they will be sorted on mergeNewEvents)
            if (pending.length) {
              setNewEvents((curr) => [...pending, ...curr])
            }

            if (appended.length) {
              // merging these will trigger a group recomputation
              setEvents((oldEvents) =>
                // insert each appended event using binary search to maintain sort order and deduplicate
                mergeReverseSortedLists(
                  oldEvents,
                  appended.sort((a, b) => b.created_at - a.created_at)
                )
              )
            }
          },
          1800,
          3000
        ),
      []
    )

    function mergeNewEvents() {
      setEvents((oldEvents) =>
        // insert each new event using binary search to maintain sort order and deduplicate
        mergeReverseSortedLists(
          oldEvents,
          newEvents.sort((a, b) => b.created_at - a.created_at)
        )
      )
      setNewEvents([])
      setShowNewNotesButton(false)
      setLastButtonClickTime(Date.now())
      setIsFirstRun(false)
      setTimeout(() => {
        scrollToTop('smooth')
      }, 0)
    }

    const list = (
      <div className="min-h-screen [overflow-anchor:none]">
        {nameFilteredGroups.map(({ totalNotes, allNoteTimestamps, topNote }) => {
          const readStatus = getReadStatus(topNote.pubkey, topNote.created_at)
          const unreadCount = getUnreadCount(topNote.pubkey, allNoteTimestamps)

          return (
            <CompactedEventCard
              key={topNote.id}
              className="w-full"
              event={topNote}
              variant={topNote.kind === kinds.Repost ? 'repost' : 'note'}
              totalNotesInTimeframe={unreadCount}
              filterMutedNotes={filterMutedNotes}
              isSelected={selectedNoteId === topNote.id}
              onSelect={() => setSelectedNoteId(topNote.id)}
              onLastNoteRead={() => {
                // If there's only one note, mark all as read instead of just last
                if (totalNotes === 1) {
                  markAllNotesRead(topNote.pubkey, topNote.created_at)
                } else {
                  markLastNoteRead(topNote.pubkey, topNote.created_at)
                }
              }}
              onAllNotesRead={() => markAllNotesRead(topNote.pubkey, topNote.created_at)}
              onMarkAsUnread={() => markAsUnread(topNote.pubkey)}
              isLastNoteRead={readStatus.isLastNoteRead}
              areAllNotesRead={readStatus.areAllNotesRead}
            />
          )
        })}
        {events.length ? (
          <div className="text-center text-sm text-muted-foreground mt-2">
            {t('end of grouped results')}
          </div>
        ) : !loading && !events.length ? (
          <div className="flex justify-center w-full mt-2">
            <Button size="lg" onClick={() => setRefreshCount((count) => count + 1)}>
              {t('reload notes')}
            </Button>
          </div>
        ) : null}
      </div>
    )

    return (
      <div>
        <div ref={topRef} className="scroll-mt-[calc(6rem+1px)]" />
        {supportTouch ? (
          <PullToRefresh
            onRefresh={async () => {
              refresh()
              await new Promise((resolve) => setTimeout(resolve, 1000))
            }}
            pullingContent=""
          >
            {list}
          </PullToRefresh>
        ) : (
          list
        )}
        <div className="h-40" />
        {showNewNotesButton && newEvents.length > 0 && (
          <NewNotesButton newEvents={newEvents} onClick={mergeNewEvents} />
        )}
      </div>
    )
  }
)
GroupedNoteList.displayName = 'GroupedNoteList'
export default GroupedNoteList

export type TGroupedNoteListRef = {
  scrollToTop: (behavior?: ScrollBehavior) => void
  refresh: () => void
}

export function calculateRelevanceScore(
  stats: NoteStatEntry | undefined,
  repliesCount: number = 0
): number {
  const reactionsCount = stats?.reactionCount ?? 0
  const repostsCount = stats?.repostCount ?? 0
  return reactionsCount + repostsCount * 3 + repliesCount * 4
}
